import { useMemo } from 'react'
import { useVideoConfig } from 'remotion'
import { Subtitles } from '../../components/Subtitles'
import { captionWordsForItems } from '../captionWords'
import { useAssets, useFollowedScenes } from '../contexts'
import { useLoadFont } from '../fonts'
import { framesToSeconds } from '../frames'
import { captionSourceOf } from '../sceneWindow'
import type { CaptionsItem, TranscriptAsset } from '../types'
import { transcriptAssetId } from '../types'

/**
 * Captions show the words of the scene they belong to (#77, #541).
 *
 * The words are computed here, from the current state of that scene, by the
 * same pure mapper on Lambda and in the editor — so a trim or a move shows
 * its captions at once, and the render shows exactly that. A cut inside the
 * scene keeps one flow: the mapper rebases the pieces back to back.
 */
export function CaptionsLayer({ item }: { item: CaptionsItem }) {
  const { fps } = useVideoConfig()
  const assets = useAssets()
  const mediaItems = useFollowedScenes(item.zealous?.sceneId, item.forTrackId)

  const words = useMemo(() => {
    const transcripts: Record<string, TranscriptAsset['words']> = {}
    for (const asset of Object.values(assets)) {
      if (asset.type === 'transcript')
        transcripts[asset.recordingId] = asset.words
    }
    const itemStart = framesToSeconds(item.from, fps)
    return captionWordsForItems(
      mediaItems.map((media) => captionSourceOf(media, assets, fps, itemStart)),
      transcripts,
    )
  }, [assets, mediaItems, item.from, fps])

  const loaded = useLoadFont({
    fontFamily: item.style.fontFamily,
    fontVariant: item.style.fontStyle ?? 'normal',
    fontWeight: String(item.style.fontWeight),
  })

  if (!loaded) return null

  // The background is the caption box's, painted on the item (Subtitles
  // would paint it on the scrolling block it measures).
  const { background, ...style } = item.style
  return (
    <div
      style={{
        position: 'absolute',
        left: item.left,
        top: item.top,
        width: item.width,
        height: item.height,
        opacity: item.opacity,
        transform: `rotate(${item.rotation}deg)`,
        backgroundColor: background,
        borderRadius: background ? Math.round(style.fontSize * 0.2) : undefined,
      }}
    >
      <Subtitles words={words} transcriptionStyle={style} />
    </div>
  )
}
